import React, { useEffect, useState, useCallback } from 'react';
import Layout from '@/components/Layout';
import ModuleIntelligenceBlock from '@/components/ModuleIntelligenceBlock';
import { leasingApi } from '@/utils/leasingApi';
import { Button } from '@/components/ui/button';
import { ArrowRight, X, UserPlus, Globe, Phone, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const STAGE_PILL = {
  lead:      'bg-slate-200 text-slate-700',
  prospect:  'bg-blue-100 text-blue-800',
  applicant: 'bg-violet-100 text-violet-800',
  approved:  'bg-emerald-100 text-emerald-800',
  rejected:  'bg-red-100 text-red-700',
  lost:      'bg-zinc-200 text-zinc-600',
};

const STAGE_ORDER = ['lead', 'prospect', 'applicant', 'approved', 'rejected', 'lost'];

export default function Prospects() {
  const [rows, setRows] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, pl] = await Promise.all([
        leasingApi.listProspects(stageFilter || undefined),
        leasingApi.pipeline(),
      ]);
      setRows(p.data || []);
      setPipeline(pl.data);
    } catch (e) { toast.error('Failed to load prospects'); }
    finally { setLoading(false); }
  }, [stageFilter]);

  useEffect(() => { load(); }, [load]);

  const onTransition = async (p, to_stage) => {
    try {
      await leasingApi.transitionProspect(p.id, { to_stage });
      toast.success(`${p.full_name} → ${to_stage}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Transition failed'); }
  };

  const onPromoteToApplicant = async (p) => {
    try {
      // Create applicant + transition prospect
      await leasingApi.createApplicant({
        prospect_id: p.id,
        full_name: p.full_name,
        email: p.email,
        phone: p.phone,
        listing_id: p.listing_id,
      });
      toast.success(`${p.full_name} promoted to Applicant`);
      navigate('/applicants');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Promotion failed'); }
  };

  return (
    <Layout>
      <div className="space-y-5" data-testid="prospects-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-[#0E1320]">Prospects</h1>
            <p className="text-[13px] text-[#71777E] mt-1">Inbound leads from public listings, walk-ins, and referrals. Promote forward in the lifecycle.</p>
          </div>
        </div>

        {/* Pipeline funnel */}
        {pipeline && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2.5" data-testid="prospects-funnel">
            {['lead', 'prospect', 'applicant', 'approved', 'move_in', 'active', 'renewal', 'move_out'].map((s, i) => (
              <button key={s} onClick={() => setStageFilter(stageFilter === s ? '' : s)}
                data-testid={`funnel-${s}`}
                className={`rounded-xl border p-3 text-start transition-all ${stageFilter === s ? 'border-[#0E1320] bg-[#0E1320] text-white' : 'border-[#E6EAF0] bg-white hover:border-[#0E1320]'}`}>
                <div className={`text-[10px] reva-mono uppercase tracking-[0.16em] ${stageFilter === s ? 'text-white/70' : 'text-[#71777E]'}`}>{String.fromCharCode(65 + i)}</div>
                <div className={`text-xl font-bold tabular-nums ${stageFilter === s ? 'text-white' : 'text-[#0E1320]'}`}>{pipeline.stages[s] || 0}</div>
                <div className={`text-[10.5px] uppercase reva-mono tracking-[0.14em] mt-0.5 ${stageFilter === s ? 'text-white/70' : 'text-[#4A5263]'}`}>{s.replace(/_/g, ' ')}</div>
              </button>
            ))}
          </div>
        )}

        {/* Prospects table */}
        <div className="rounded-xl border border-[#E6EAF0] bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-[#ECEFF3] text-[12px] reva-mono text-[#71777E] flex items-center gap-2">
            {stageFilter && <>Filtered by stage: <span className="text-[#0E1320] font-semibold">{stageFilter}</span> <button onClick={() => setStageFilter('')}><X size={12} /></button></>}
            <span className="ms-auto">{rows.length} {rows.length === 1 ? 'record' : 'records'}</span>
          </div>
          <table className="w-full text-[13px]">
            <thead className="bg-[#FBFCFD] border-b border-[#ECEFF3]">
              <tr>
                {['Contact', 'Source', 'Stage', 'Created', ''].map(h => (
                  <th key={h} className="text-start px-3 py-2.5 text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E] font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={5} className="text-center py-10 text-[#71777E]">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={5} className="text-center py-10 text-[#71777E]">No prospects in this stage.</td></tr>
              : rows.map(p => (
                <tr key={p.id} data-testid={`prospect-row-${p.id}`} className="border-b border-[#F1F4F7] hover:bg-[#F7FBF9]">
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-[#0E1320]">{p.full_name}</div>
                    <div className="text-[11px] text-[#71777E] inline-flex items-center gap-2 mt-0.5">
                      {p.phone && <span className="inline-flex items-center gap-1"><Phone size={9} /> {p.phone}</span>}
                      {p.email && <span className="inline-flex items-center gap-1"><Mail size={9} /> {p.email}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    {p.source === 'public' ? <span className="inline-flex items-center gap-1 text-[11.5px] text-[#00734A]"><Globe size={10} /> Public listing</span> : <span className="text-[11.5px] text-[#71777E]">{p.source}</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium reva-mono ${STAGE_PILL[p.lifecycle_stage] || 'bg-slate-100'}`}>{p.lifecycle_stage}</span>
                  </td>
                  <td className="px-3 py-2.5 text-[11.5px] text-[#71777E] reva-mono inline-flex items-center gap-1">
                    <Clock size={10} /> {new Date(p.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      {p.lifecycle_stage === 'lead' && (
                        <Button size="sm" variant="outline" onClick={() => onTransition(p, 'prospect')} data-testid={`prospect-qualify-${p.id}`}>
                          Qualify <ArrowRight size={12} className="ms-1" />
                        </Button>
                      )}
                      {['lead', 'prospect'].includes(p.lifecycle_stage) && (
                        <Button size="sm" onClick={() => onPromoteToApplicant(p)} data-testid={`prospect-promote-${p.id}`}>
                          <UserPlus size={12} className="mr-1" /> Applicant
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ModuleIntelligenceBlock module="leases" testId="prospects-intel" />
      </div>
    </Layout>
  );
}
